import dayjs from "dayjs";
import { useMemo, useState } from "react";

import type { ToggleButtonItem } from "../../Common/ToggleButton";
import type {
  ClientsPageAnalytics,
  ReturnedTranslatedContentClientsPageHook,
} from "../../types";

import { useDebounceFn } from "../../..";
import { ErrorContent } from "../../../ErrorContent";
import { useFeatureFlags, useIsMobile } from "../../../hooks";
import { ResponsiveSpinner } from "../../../ResponsiveSpinner";
import { BarChart } from "../../Common/BarChart";
import { ToggleButton } from "../../Common/ToggleButton";
import { formatNumberV2, formatToK } from "../../utils";
import { useClientTradingReportContext } from "../providers/TradingReportProvider";
import {
  formatUSDCompactAndSymbol,
  formatUSDWithUSAndSymbol,
} from "../utils/formatters";
import { useDateRange } from "../utils/useDateRange";
import { BarChartTooltipMemo } from "./BarChartTooltip";
import { ChartSectionEntry } from "./ChartSectionEntry";
import { ClientTradingMobileChartV2 } from "./ClientTradingMobileChartV2";
import { TradingByLineChart } from "./TradingByLineChart";

type ClientTradingGraphsProps = {
  analytics?: ClientsPageAnalytics;
  translations: ReturnedTranslatedContentClientsPageHook;
};

type InstrumentBarChartItem = {
  instrumentType: keyof typeof BARCHART_COLOR_CONFIG | string;
  clientCount: number;
  totalVolume: number;
  totalVolumeUSD: number;
};

const FIRST_MODE_ID = "usd" as const;
const SECOND_MODE_ID = "lots" as const;
const THIRD_MODE_ID = "clients" as const;

type ChartType = "usd" | "lots" | "clients";

const BARCHART_COLOR_CONFIG = {
  FX: "#F8CE98",
  XAU: "#E3C0F0",
  XAG: "#99C1FD",
  Oil: "#B4F59C",
  CFD: "#F2AFA9",
  Equities: "#FFD6E8",
  SB: "#A8E6CF",
  Crypto: "#FFF3B0",
  Index: "#C6F1E7",
  Commodity: "#D1C4E9",
};

const TradingReportChartsLoading = ({ isMobile }: { isMobile: boolean }) => (
  <div
    // Mobile: the filter bar above already contributes 16px via its own
    // pb-4 -- no margin here, or the two stack into a 56px gap instead of
    // the intended 16px. Desktop keeps mt-[40px] (no such padding above it).
    className={`grid w-full ${isMobile ? "mt-0 grid-cols-1 gap-4" : "mt-[40px] grid-cols-2 gap-6"}`}
  >
    {Array.from({ length: 2 }, (_, index) => (
      <div
        key={index}
        className={`flex items-center justify-center rounded-2xl bg-surface-brand-subtle ${
          isMobile ? "h-[101px]" : "h-[422px]"
        }`}
      >
        <ResponsiveSpinner
          width={isMobile ? 24 : 40}
          height={isMobile ? 24 : 40}
        />
      </div>
    ))}
  </div>
);

export const ClientTradingChart = ({
  analytics,
  translations,
}: ClientTradingGraphsProps) => {
  const { language, translatedContent } = translations;
  const isMobile = useIsMobile();

  const {
    state: { dateRange },
    resources: { graphs },
    runResource,
  } = useClientTradingReportContext();
  const dateTitle = useDateRange(dateRange, translations.language);

  const { error } = graphs;
  const { graph, instrument } = graphs.data ?? {};
  const isLoading = graphs.status === "loading";

  const { mobileUsabilityEnhancement } = useFeatureFlags();

  const graphsVolumeTradedModeOptions: ToggleButtonItem[] = [
    { id: FIRST_MODE_ID, label: "USD" },
    { id: SECOND_MODE_ID, label: translatedContent.graphs.lotsToggle ?? "" },
    { id: THIRD_MODE_ID, label: translatedContent.graphs.clients ?? "" },
  ];

  const [debounceTooltipChartsTrack] = useDebounceFn(() => {
    analytics?.trackTableChartEvent(isMobile, "Trading report");
  }, 2000);

  const [
    selectedGraphsTotalVolumeTradedMode,
    setSelectedGraphsTotalVolumeTradedMode,
  ] = useState<ToggleButtonItem>(graphsVolumeTradedModeOptions[0]);

  const graphsVolumeTradedLineChartData: Record<
    string,
    {
      title: string;
      suffix?: string;
      total: number;
      chart: { value: number; date: string }[];
      format?: (value: number) => string;
      tooltipFormat?: (value: number) => string;
    }
  > = useMemo(
    () => ({
      usd: {
        title: translatedContent.graphs.totalVolumeTraded,
        total: graph?.tradingBy?.volume?.usd.total ?? 0,
        chart: graph?.tradingBy?.volume?.usd.data ?? [],
        suffix: "USD",
        format: formatUSDCompactAndSymbol,
        tooltipFormat: formatUSDWithUSAndSymbol,
      },
      lots: {
        title: translatedContent.graphs.totalVolumeTraded,
        suffix: translatedContent.graphs.lots.toLowerCase(),
        total: graph?.tradingBy?.volume?.lots.total ?? 0,
        chart: graph?.tradingBy?.volume?.lots.data ?? [],
        format: (value: number) => formatToK(value),
      },
      clients: {
        title: translatedContent.graphs.tradedByClients,
        total: graph?.tradingBy?.clients.total ?? 0,
        chart: graph?.tradingBy?.clients.data ?? [],
      },
    }),
    [
      translatedContent.graphs.totalVolumeTraded,
      translatedContent.graphs.lots,
      translatedContent.graphs.tradedByClients,
      graph?.tradingBy?.volume?.usd.total,
      graph?.tradingBy?.volume?.usd.data,
      graph?.tradingBy?.volume?.lots.total,
      graph?.tradingBy?.volume?.lots.data,
      graph?.tradingBy?.clients.total,
      graph?.tradingBy?.clients.data,
    ],
  );

  const lineChartData = useMemo(() => {
    return graphsVolumeTradedLineChartData[
      selectedGraphsTotalVolumeTradedMode.id
    ];
  }, [graphsVolumeTradedLineChartData, selectedGraphsTotalVolumeTradedMode]);

  const graphsInstrumentBarChartData = useMemo(() => {
    if (Array.isArray(instrument) && instrument.length) {
      return instrument.map((item: InstrumentBarChartItem) => ({
        label: item.instrumentType,
        color:
          BARCHART_COLOR_CONFIG[
            item.instrumentType as keyof typeof BARCHART_COLOR_CONFIG
          ],
        clients: [],
        clientCount: item.clientCount,
        volume: formatNumberV2(item.totalVolume, language, true),
        volumeUSD: formatNumberV2(item.totalVolumeUSD, language, true),
        suffix: "lots",
      }));
    }
    return [];
  }, [instrument, language]);

  if (isLoading) {
    return <TradingReportChartsLoading isMobile={isMobile} />;
  }

  if (error) {
    return (
      <div className="mt-[40px] flex w-full flex-col items-center rounded-[16px] bg-surface-brand-subtle p-8">
        <ErrorContent action={() => runResource("graphs")} kind="chart" />
      </div>
    );
  }

  if (isMobile && mobileUsabilityEnhancement) {
    return (
      <ClientTradingMobileChartV2
        lineChartData={lineChartData}
        graphsInstrumentBarChartData={graphsInstrumentBarChartData}
        graphsVolumeTradedModeOptions={graphsVolumeTradedModeOptions}
        selectedGraphsTotalVolumeTradedMode={
          selectedGraphsTotalVolumeTradedMode
        }
        onToggleChange={setSelectedGraphsTotalVolumeTradedMode}
        dateRange={{
          from: dayjs(dateRange.from).format("YYYY-MM-DD"),
          to: dayjs(dateRange.to).format("YYYY-MM-DD"),
        }}
        dateTitle={dateTitle}
        translatedContent={translatedContent}
        analytics={analytics}
        debounceTooltipChartsTrack={debounceTooltipChartsTrack}
      />
    );
  }

  return (
    lineChartData.total > 0 && (
      <div
        data-testid="chart-section"
        className="mt-[40px] grid w-full grid-cols-1 gap-6 min-[1200px]:grid-cols-2"
      >
        <ChartSectionEntry
          title={lineChartData.title}
          isMobile={isMobile}
          total={lineChartData.total}
          totalCurrencyCode={lineChartData.suffix}
          toggle={
            <ToggleButton
              items={graphsVolumeTradedModeOptions}
              selectedValue={selectedGraphsTotalVolumeTradedMode}
              onClick={(item) => {
                setSelectedGraphsTotalVolumeTradedMode(item);
                analytics?.trackTableChartTradingEvent(item.id);
              }}
            />
          }
          chart={
            <TradingByLineChart
              title={lineChartData.title}
              type={selectedGraphsTotalVolumeTradedMode.id as ChartType}
              data={lineChartData.chart}
              unit={lineChartData.suffix}
              format={lineChartData.format}
              tooltipFormat={lineChartData.tooltipFormat}
              startDate={dayjs(dateRange.from).format("YYYY-MM-DD")}
              endDate={dayjs(dateRange.to).format("YYYY-MM-DD")}
              onHover={debounceTooltipChartsTrack}
            />
          }
        />
        <ChartSectionEntry
          title={translatedContent.graphs.tradedInstrumentsClass}
          subtitle={dateTitle}
          isMobile={isMobile}
          chart={
            <BarChart
              data={graphsInstrumentBarChartData}
              translations={translatedContent.graphs}
              tooltip={({ value }) => (
                <BarChartTooltipMemo
                  translations={translatedContent.graphs}
                  data={value}
                />
              )}
              onHover={debounceTooltipChartsTrack}
            />
          }
        />
      </div>
    )
  );
};
